import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, FileText, Image as ImageIcon, Table, Map, FileCode, Archive } from "lucide-react";
import { DocumentoGDB } from "@/hooks/useDocumentosGDB";
import { MapViewer } from "./viewers/MapViewer";
import { ExcelViewer } from "./viewers/ExcelViewer";
import { ZipViewer } from "./viewers/ZipViewer";

interface VisualizadorGDBProps {
  documento: DocumentoGDB | null;
  isOpen: boolean;
  onClose: () => void;
}

export const VisualizadorGDB: React.FC<VisualizadorGDBProps> = ({ documento, isOpen, onClose }) => {
  if (!documento) return null;

  const getTipoVisor = (tipoArchivo: string) => {
    const imagenes = ['.jpg', '.jpeg', '.png', '.tif', '.tiff'];
    const gis = ['.mxd', '.gdb', '.mpk', '.shp'];
    const cad = ['.dwg', '.dxf'];
    const mapas = ['.kml', '.kmz'];
    const documentos = ['.pdf'];
    const hojas = ['.xlsx', '.xls'];
    const archivos = ['.zip'];

    if (imagenes.includes(tipoArchivo)) return 'imagen';
    if (mapas.includes(tipoArchivo)) return 'mapa';
    if (hojas.includes(tipoArchivo)) return 'excel';
    if (archivos.includes(tipoArchivo)) return 'zip';
    if (gis.includes(tipoArchivo)) return 'gis';
    if (cad.includes(tipoArchivo)) return 'cad';
    if (documentos.includes(tipoArchivo)) return 'pdf';
    return 'descarga';
  };

  const tipoVisor = getTipoVisor(documento.tipo_archivo);

  const renderVisor = () => {
    switch (tipoVisor) {
      case 'imagen':
        return (
          <div className="flex items-center justify-center bg-muted rounded-lg p-4">
            <img
              src={documento.url_archivo}
              alt={documento.nombre_documento}
              className="max-w-full max-h-[600px] object-contain"
              onError={(e) => {
                console.error('Error cargando imagen:', documento.url_archivo);
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="text-center py-8">
                      <p class="text-muted-foreground mb-4">No se pudo cargar la vista previa de la imagen</p>
                      <p class="text-sm text-muted-foreground">Formato: ${documento.tipo_archivo}</p>
                    </div>
                  `;
                }
              }}
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="space-y-4">
            <iframe
              src={documento.url_archivo}
              className="w-full h-[600px] rounded-lg border"
              title={documento.nombre_documento}
              onError={() => {
                console.error('Error cargando PDF:', documento.url_archivo);
              }}
            />
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Si no se visualiza el PDF, <Button variant="link" className="p-0 h-auto" onClick={() => window.open(documento.url_archivo, '_blank')}>haz clic aquí para abrirlo</Button>
              </p>
            </div>
          </div>
        );

      case 'mapa':
        return <MapViewer url={documento.url_archivo} fileName={documento.nombre_archivo} />;

      case 'excel':
        return <ExcelViewer url={documento.url_archivo} />;

      case 'zip':
        return <ZipViewer url={documento.url_archivo} />;

      case 'gis':
        return (
          <div className="border rounded-lg p-8 text-center space-y-4">
            <Map className="mx-auto h-16 w-16 text-muted-foreground" />
            <div>
              <h3 className="font-semibold text-lg mb-2">Archivo GIS</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Este archivo requiere software especializado como ArcGIS, QGIS o ArcGIS Pro para visualizarlo.
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Tipo: {documento.tipo_archivo.toUpperCase()}
              </p>
              <Button onClick={() => window.open(documento.url_archivo, '_blank')}>
                <Download className="mr-2 h-4 w-4" />
                Descargar Archivo
              </Button>
            </div>
          </div>
        );

      case 'cad':
        return (
          <div className="border rounded-lg p-8 text-center space-y-4">
            <FileCode className="mx-auto h-16 w-16 text-muted-foreground" />
            <div>
              <h3 className="font-semibold text-lg mb-2">Archivo CAD</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Este archivo requiere software CAD como AutoCAD o DraftSight para visualizarlo.
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Tipo: {documento.tipo_archivo.toUpperCase()}
              </p>
              <Button onClick={() => window.open(documento.url_archivo, '_blank')}>
                <Download className="mr-2 h-4 w-4" />
                Descargar Archivo
              </Button>
            </div>
          </div>
        );

      default:
        return (
          <div className="border rounded-lg p-8 text-center space-y-4">
            <FileText className="mx-auto h-16 w-16 text-muted-foreground" />
            <div>
              <h3 className="font-semibold text-lg mb-2">Vista Previa No Disponible</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Descarga el archivo para visualizarlo.
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Tipo: {documento.tipo_archivo.toUpperCase()}
              </p>
              <Button onClick={() => window.open(documento.url_archivo, '_blank')}>
                <Download className="mr-2 h-4 w-4" />
                Descargar Archivo
              </Button>
            </div>
          </div>
        );
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{documento.nombre_documento}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(documento.url_archivo, '_blank')}
            >
              <Download className="mr-2 h-4 w-4" />
              Descargar
            </Button>
          </div>
        </DialogHeader>

        <Tabs defaultValue="preview" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview">Vista Previa</TabsTrigger>
            <TabsTrigger value="metadata">Metadatos</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="space-y-4">
            {renderVisor()}
          </TabsContent>

          <TabsContent value="metadata" className="space-y-4">
            <div className="border rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Categoría</p>
                  <p className="text-sm">{documento.categoria}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Tipo de Archivo</p>
                  <p className="text-sm">{documento.tipo_archivo}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Tamaño</p>
                  <p className="text-sm">{formatBytes(documento.tamano_archivo)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Fecha de Subida</p>
                  <p className="text-sm">
                    {new Date(documento.created_at).toLocaleDateString('es-CO')}
                  </p>
                </div>
              </div>

              {documento.descripcion && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Descripción</p>
                  <p className="text-sm">{documento.descripcion}</p>
                </div>
              )}

              {(documento.sistema_coordenadas || documento.proyeccion || documento.escala) && (
                <>
                  <hr />
                  <h4 className="font-semibold">Información Geográfica</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {documento.sistema_coordenadas && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Sistema de Coordenadas</p>
                        <p className="text-sm">{documento.sistema_coordenadas}</p>
                      </div>
                    )}
                    {documento.proyeccion && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Proyección</p>
                        <p className="text-sm">{documento.proyeccion}</p>
                      </div>
                    )}
                    {documento.escala && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Escala</p>
                        <p className="text-sm">{documento.escala}</p>
                      </div>
                    )}
                    {documento.fecha_captura && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Fecha de Captura</p>
                        <p className="text-sm">
                          {new Date(documento.fecha_captura).toLocaleDateString('es-CO')}
                        </p>
                      </div>
                    )}
                    {documento.fuente_informacion && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Fuente</p>
                        <p className="text-sm">{documento.fuente_informacion}</p>
                      </div>
                    )}
                    {documento.resolucion_espacial && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Resolución Espacial</p>
                        <p className="text-sm">{documento.resolucion_espacial}</p>
                      </div>
                    )}
                  </div>
                  {documento.area_cobertura && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Área de Cobertura</p>
                      <p className="text-sm">{documento.area_cobertura}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
